import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { staffService } from '../../../services/staff/StaffService';
import { EmailSenderIdentity } from '../../../services/email/types';
import { supabase } from '../../services/supabase';

interface HeaderProps {
  user: any;
  onSignOut: () => void;
}

export const Header: React.FC<HeaderProps> = ({ user, onSignOut }) => {
  const location = useLocation();
  const [staffData, setStaffData] = useState<EmailSenderIdentity | null>(null);
  // Posteingang (05.10.2026): Der Menüpunkt erscheint nur für Mitarbeiter mit
  // dem Merkmal darf_posteingang_sehen. Die Anzeige ist eine Bequemlichkeit;
  // die Berechtigung prüft die Netlify-Funktion posteingang anhand der
  // persönlichen Anmeldung.
  const [darfPosteingang, setDarfPosteingang] = useState(false);
  // Beitragsrechnung (30.10.2026): gleiches Muster mit dem Merkmal
  // darf_beitragsrechnung_sehen; die Berechtigung prüft die Netlify-Funktion
  // beitragsrechnung.
  const [darfBeitragsrechnung, setDarfBeitragsrechnung] = useState(false);
  
  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    const loadStaffData = async () => {
      if (!user?.email) return;
      
      try {
        const allStaff = await staffService.getActiveStaff();
        const currentStaff = allStaff.find(
          staff => staff.email.toLowerCase() === user.email.toLowerCase()
        );
        
        if (currentStaff) {
          setStaffData(currentStaff);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Staff-Daten:', error);
      }
    };

    loadStaffData();
  }, [user?.email]);

  useEffect(() => {
    const email = String(user?.email || '').trim().toLowerCase();
    if (!email) {
      setDarfPosteingang(false);
      setDarfBeitragsrechnung(false);
      return;
    }
    supabase
      .from('xtax_staff')
      .select('darf_posteingang_sehen, darf_beitragsrechnung_sehen')
      .ilike('email', email)
      .maybeSingle()
      .then(({ data }) => {
        setDarfPosteingang(data?.darf_posteingang_sehen === true);
        setDarfBeitragsrechnung(data?.darf_beitragsrechnung_sehen === true);
      });
  }, [user?.email]);

  const isTimWolter = user?.email === 'tim.wolter@xtax.de';
  const userName = staffData?.fullName || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const userEmail = user?.email || '';
  const userPhotoUrl = staffData?.photoUrl;

  return (
    <div style={{
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: '#ffffff',
      padding: '16px 24px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
          <img 
            src="https://xtax.de/XTAX.png" 
            alt="XTAX Logo" 
            style={{height: '32px'}}
          />
          <h1 style={{margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827'}}>
            Intranet
          </h1>
        </div>

        <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
          <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
            {userPhotoUrl && (
              <img
                src={userPhotoUrl}
                alt={userName}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  const parent = target.parentElement;
                  if (parent) {
                    target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.style.width = '32px';
                    fallback.style.height = '32px';
                    fallback.style.borderRadius = '50%';
                    fallback.style.backgroundColor = '#6366f1';
                    fallback.style.color = '#fff';
                    fallback.style.display = 'flex';
                    fallback.style.alignItems = 'center';
                    fallback.style.justifyContent = 'center';
                    fallback.style.fontSize = '14px';
                    fallback.style.fontWeight = 'bold';
                    fallback.textContent = userName.charAt(0).toUpperCase();
                    parent.appendChild(fallback);
                  }
                }}
              />
            )}
            <div>
              <div style={{fontSize: '14px', fontWeight: '500', color: '#111827'}}>
                {userName}
              </div>
              <div style={{fontSize: '12px', color: '#6B7280'}}>{userEmail}</div>
            </div>
          </div>
          <button
            onClick={onSignOut}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4B5563',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Abmelden
          </button>
        </div>
      </div>
      
      <div style={{
        marginTop: '20px',
        display: 'flex',
        gap: '4px',
        borderBottom: '2px solid #E5E7EB'
      }}>
        {isTimWolter && (
          <Link
            to="/tickets"
            style={{
              padding: '8px 16px',
              textDecoration: 'none',
              color: isActive('/tickets') ? '#2563EB' : '#6B7280',
              fontWeight: isActive('/tickets') ? '600' : '400',
              fontSize: '14px',
              borderBottom: isActive('/tickets') ? '2px solid #2563EB' : '2px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.2s'
            }}
          >
            Tickets
          </Link>
        )}

        <Link 
          to="/"
          style={{
            padding: '8px 16px',
            textDecoration: 'none',
            color: isActive('/') ? '#2563EB' : '#6B7280',
            fontWeight: isActive('/') ? '600' : '400',
            fontSize: '14px',
            borderBottom: isActive('/') ? '2px solid #2563EB' : '2px solid transparent',
            marginBottom: '-2px',
            transition: 'all 0.2s'
          }}
        >
          Fristen
        </Link>

        <Link 
          to="/kundenstammdaten"
          style={{
            padding: '8px 16px',
            textDecoration: 'none',
            color: isActive('/kundenstammdaten') ? '#2563EB' : '#6B7280',
            fontWeight: isActive('/kundenstammdaten') ? '600' : '400',
            fontSize: '14px',
            borderBottom: isActive('/kundenstammdaten') ? '2px solid #2563EB' : '2px solid transparent',
            marginBottom: '-2px',
            transition: 'all 0.2s'
          }}
        >
          Kundenstammdaten
        </Link>
        
        <Link 
          to="/kommunikation"
          style={{
            padding: '8px 16px',
            textDecoration: 'none',
            color: isActive('/kommunikation') ? '#2563EB' : '#6B7280',
            fontWeight: isActive('/kommunikation') ? '600' : '400',
            fontSize: '14px',
            borderBottom: isActive('/kommunikation') ? '2px solid #2563EB' : '2px solid transparent',
            marginBottom: '-2px',
            transition: 'all 0.2s'
          }}
        >
          Kommunikation
        </Link>

        {isTimWolter && (
          <Link 
            to="/rechnungen"
            style={{
              padding: '8px 16px',
              textDecoration: 'none',
              color: isActive('/rechnungen') ? '#2563EB' : '#6B7280',
              fontWeight: isActive('/rechnungen') ? '600' : '400',
              fontSize: '14px',
              borderBottom: isActive('/rechnungen') ? '2px solid #2563EB' : '2px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.2s'
            }}
          >
            Rechnungen
          </Link>
        )}

        {isTimWolter && (
          <Link 
            to="/umsatzanalyse"
            style={{
              padding: '8px 16px',
              textDecoration: 'none',
              color: isActive('/umsatzanalyse') ? '#2563EB' : '#6B7280',
              fontWeight: isActive('/umsatzanalyse') ? '600' : '400',
              fontSize: '14px',
              borderBottom: isActive('/umsatzanalyse') ? '2px solid #2563EB' : '2px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.2s'
            }}
          >
            Umsatzanalyse
          </Link>
        )}

        {darfBeitragsrechnung && (
          <Link 
            to="/beitragsrechnung"
            style={{
              padding: '8px 16px',
              textDecoration: 'none',
              color: isActive('/beitragsrechnung') ? '#2563EB' : '#6B7280',
              fontWeight: isActive('/beitragsrechnung') ? '600' : '400',
              fontSize: '14px',
              borderBottom: isActive('/beitragsrechnung') ? '2px solid #2563EB' : '2px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.2s'
            }}
          >
            Beitragsrechnung
          </Link>
        )}

        <Link 
          to="/urlaubsplaner"
          style={{
            padding: '8px 16px',
            textDecoration: 'none',
            color: isActive('/urlaubsplaner') ? '#2563EB' : '#6B7280',
            fontWeight: isActive('/urlaubsplaner') ? '600' : '400',
            fontSize: '14px',
            borderBottom: isActive('/urlaubsplaner') ? '2px solid #2563EB' : '2px solid transparent',
            marginBottom: '-2px',
            transition: 'all 0.2s'
          }}
        >
          Urlaubsplaner
        </Link>

        {isTimWolter && (
          <Link 
            to="/datev"
            style={{
              padding: '8px 16px',
              textDecoration: 'none',
              color: isActive('/datev') ? '#2563EB' : '#6B7280',
              fontWeight: isActive('/datev') ? '600' : '400',
              fontSize: '14px',
              borderBottom: isActive('/datev') ? '2px solid #2563EB' : '2px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.2s'
            }}
          >
            DATEV
          </Link>
        )}

        {darfPosteingang && (
          <Link 
            to="/posteingang"
            style={{
              padding: '8px 16px',
              textDecoration: 'none',
              color: isActive('/posteingang') ? '#2563EB' : '#6B7280',
              fontWeight: isActive('/posteingang') ? '600' : '400',
              fontSize: '14px',
              borderBottom: isActive('/posteingang') ? '2px solid #2563EB' : '2px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.2s'
            }}
          >
            Posteingang
          </Link>
        )}
      </div>
    </div>
  );
};
